/**
 * A deposit: the shopper buys the store's currency at face value (1:1, every amount in US cents),
 * typing any amount between the store's minimum and maximum or tapping one of its presets. Distinct
 * from a bundle (`balance_packs`, a priced offer that may credit more than it costs).
 *
 * The settings are resolved like the shipping config: the platform default on
 * `platform_settings.deposit` (`DEPOSIT_SETTINGS_DEFAULT` until staff save one), overridden per
 * environment on `storefront_configs.deposit` (NULL = inherit). Whatever either saves stays inside
 * the hard bounds here, which no setting can widen: Stripe's own minimum charge, and a ceiling.
 */
import { z } from "zod";

/** The least any deposit can be: Stripe's minimum charge in US dollars. */
export const DEPOSIT_FLOOR_CENTS = 50;
/** The most any deposit can be, whatever a store sets. */
export const DEPOSIT_CEILING_CENTS = 1_000_000;
/**
 * How many presets a store offers at most: as many as sit side by side in the dialog on a 320px
 * phone (the prototype's five, "$50" to "$1,000"). More, or wider figures, is ENG-7801.
 */
export const DEPOSIT_MAX_PRESETS = 5;

const boundedCents = z.int().min(DEPOSIT_FLOOR_CENTS).max(DEPOSIT_CEILING_CENTS);

/** Why deposit settings were refused, one member per cause. */
export type DepositSettingsIssue =
  | "min_above_max"
  | "presets_not_ascending"
  | "preset_below_min"
  | "preset_above_max";

/** What is wrong with a set of deposit settings, or null; the first problem found. */
export function depositSettingsIssue(settings: {
  presetsCents: readonly number[];
  minCents: number;
  maxCents: number;
}): DepositSettingsIssue | null {
  if (settings.minCents > settings.maxCents) return "min_above_max";
  for (let i = 1; i < settings.presetsCents.length; i++) {
    if ((settings.presetsCents[i] ?? 0) <= (settings.presetsCents[i - 1] ?? 0)) {
      return "presets_not_ascending";
    }
  }
  if (settings.presetsCents.some((cents) => cents < settings.minCents)) return "preset_below_min";
  if (settings.presetsCents.some((cents) => cents > settings.maxCents)) return "preset_above_max";
  return null;
}

/** Each issue as an editor reads it. */
export const DEPOSIT_SETTINGS_ISSUE_MESSAGES: Record<DepositSettingsIssue, string> = {
  min_above_max: "The minimum is more than the maximum.",
  presets_not_ascending: "List the presets from smallest to largest, each once.",
  preset_below_min: "A preset is under the minimum.",
  preset_above_max: "A preset is over the maximum.",
};

/** The stored and wire shape: the presets, smallest first, and the least and most a deposit is. */
export const depositSettingsSchema = z
  .object({
    presetsCents: z.array(boundedCents).min(1).max(DEPOSIT_MAX_PRESETS),
    minCents: boundedCents,
    maxCents: boundedCents,
  })
  .superRefine((settings, ctx) => {
    const issue = depositSettingsIssue(settings);
    if (issue) ctx.addIssue({ code: "custom", message: DEPOSIT_SETTINGS_ISSUE_MESSAGES[issue] });
  });
export type DepositSettings = z.infer<typeof depositSettingsSchema>;

/** Every store's deposit settings until platform staff save a default: the design's. */
export const DEPOSIT_SETTINGS_DEFAULT: DepositSettings = {
  presetsCents: [5_000, 10_000, 25_000, 50_000, 100_000],
  minCents: 500,
  maxCents: 100_000,
};

/** Why an amount can't be deposited here, or null when it can. */
export function depositAmountIssue(
  cents: number,
  settings: Pick<DepositSettings, "minCents" | "maxCents">,
): "below_minimum" | "above_maximum" | null {
  if (cents < settings.minCents) return "below_minimum";
  if (cents > settings.maxCents) return "above_maximum";
  return null;
}
